// import React from 'react';
import { styled } from '@mui/material/styles';
import { EuTaxonomyClobDto, AffinityDto } from 'models/dtoTypes';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import SubSection from './_subSection';
import Empty from './_empty';
import { taxonomySvg } from 'kosmos/images';
import EsgLegand from './_esgLegand';
import { ISdgData } from './_sdgScoreClob';
import EuOptionSlider from './_euOptionSlider';
import { useTranslation } from 'react-i18next';

const ItemRoot = styled('div')(() => ({
  position: 'relative',
  maxWidth: 900,
  lineHeight: 2,
  marginBottom: 15,
}));

interface IProps {
  name?: string;
  euTaxonomyReport: EuTaxonomyClobDto[];
}

export default function DoNoSignificantHarmReport(props: IProps): JSX.Element {
  const { t } = useTranslation('default');
  const { name = t('eu-taxonomy-score'), euTaxonomyReport } = props;

  const getOption = (affinity?: AffinityDto): number => {
    switch (affinity) {
      case 'unscored':
        return -1;
      case 'negative':
        return 0;
      case 'warning':
        return 1;
      case 'neutral':
        return 2;
      case 'positive':
        return 3;
      default:
        return -1;
    }
  };

  const renderSingleListItem = (item: EuTaxonomyClobDto, index: number) => {
    const { title, affinity } = item;
    const option = getOption(affinity);
    return (
      <ItemRoot key={index}>
        <EuOptionSlider title={title || ''} option={option} />
      </ItemRoot>
    );
  };

  const renderLegands = () => {
    const legands: ISdgData[] = [
      {
        affinity: 'negative',
        title: t(
          'activity-found-to-cause-significant-harm-against-corresponding-eu-taxonomy-regulation',
        ),
        size: 'small',
      },
      {
        affinity: 'warning',
        title: t(
          'activity-found-to-cause-significant-harm-against-corresponding-eu-taxonomy-regulation-but-there-is-an-opportunity-for-improvement',
        ),
        size: 'small',
      },
      {
        affinity: 'positive',
        title: t('activity-found-to-positively-contribute-to-corresponding-eu-taxonomy-regulation'),
        size: 'small',
      },
      {
        affinity: 'neutral',
        title: t('no-significant-harm-found-against-corresponding-eu-taxonomy-regulation'),
        size: 'small',
      },
    ];

    return (
      <EsgLegand
        legands={legands}
        title={t('activity-check-against-eu-taxonomy-regulation')}
        learnMoreTabId="eu"
      />
    );
  };
  const renderAllListItems = () => {
    return (
      <Box
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="column" spacing={6}>
          {euTaxonomyReport.map(renderSingleListItem)}
        </Stack>
      </Box>
    );
  };
  const renderContent = () => {
    if (euTaxonomyReport.length === 0) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return renderAllListItems();
  };

  return (
    <SubSection title={name} icon={<img src={taxonomySvg} />}>
      <>
        {renderLegands()}
        {renderContent()}
        <p>
          *{' '}
          {t(
            'please-note-that-the-full-eu-taxonomy-regulation-is-still-to-be-agreed-by-the-eu-some-industries-are-not-yet-covered-by-eu-taxonomy-regulation-in-such-cases-activity-is-marked-as-no-significant-harm-eu-taxonomy-regulation-scores-offer-an-indicator-based-on-the-best-available-data',
          )}{' '}
        </p>
      </>
    </SubSection>
  );
}
